import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import SocialShare from "../components/SocialShare";
import ReferFriend from "../components/ReferFriend";
import { Link } from "react-router-dom";
import { Building2, ShieldCheck, Globe, Users, Zap, ArrowRight, Mail } from "lucide-react";

const SUPPORT_EMAIL = "hello@fastprototype.ai";

const Stat = ({ value, label }) => (
  <div className="bg-white border border-zinc-200 rounded-2xl p-5 text-center">
    <div className="text-2xl font-extrabold text-zinc-950">{value}</div>
    <div className="text-xs text-zinc-500 mt-1">{label}</div>
  </div>
);

const Value = ({ icon: Icon, title, body }) => (
  <div className="flex items-start gap-3">
    <div className="w-9 h-9 rounded-xl bg-zinc-100 flex items-center justify-center flex-shrink-0">
      <Icon size={16} className="text-zinc-700" />
    </div>
    <div>
      <div className="font-bold text-zinc-950 text-sm">{title}</div>
      <p className="text-xs text-zinc-600 mt-1 leading-relaxed">{body}</p>
    </div>
  </div>
);

export default function About() {
  return (
    <div className="min-h-screen bg-zinc-50" data-testid="about-page">
      <Navbar />

      <section className="max-w-4xl mx-auto px-4 pt-32 pb-12">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white border border-zinc-200 text-zinc-700 text-xs font-semibold mb-5" data-testid="made-in-eu-badge">
          <span aria-hidden="true">🇪🇺</span> Made in the European Union
        </div>
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-zinc-950 leading-[1.05]">
          We turn ideas into <span className="bg-gradient-to-r from-emerald-600 to-teal-500 bg-clip-text text-transparent">working software</span>, fast.
        </h1>
        <p className="text-lg text-zinc-600 mt-6 max-w-2xl leading-relaxed">
          FastPrototype.ai is a European team that builds production-grade MVPs in 1–4 weeks by combining AI code generation with senior developer oversight. Fixed price, fixed scope, no runaway hourly bills.
        </p>
      </section>

      <section className="max-w-4xl mx-auto px-4 pb-12">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Stat value="EU" label="Jurisdiction" />
          <Stat value="1–4 wk" label="Typical delivery" />
          <Stat value="100%" label="Client IP ownership" />
          <Stat value="GDPR" label="Compliant by default" />
        </div>
      </section>

      <section className="max-w-4xl mx-auto px-4 py-12">
        <div className="bg-white border border-zinc-200 rounded-3xl p-8">
          <div className="flex items-center gap-2 mb-4">
            <Building2 size={18} className="text-zinc-700" />
            <h2 className="text-lg font-bold text-zinc-950">Where we're based</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 text-sm" data-testid="company-info">
            <div>
              <div className="text-xs uppercase tracking-wider font-bold text-zinc-400 mb-1">Registered in</div>
              <div className="text-zinc-900 font-semibold">European Union</div>
            </div>
            <div>
              <div className="text-xs uppercase tracking-wider font-bold text-zinc-400 mb-1">Contact</div>
              <a href={`mailto:${SUPPORT_EMAIL}`} className="text-zinc-900 font-semibold inline-flex items-center gap-1 hover:underline" data-testid="contact-email-link">
                <Mail size={12} /> {SUPPORT_EMAIL}
              </a>
            </div>
          </div>
          <p className="text-[11px] text-zinc-500 mt-5 border-t border-zinc-100 pt-4">
            All contracts are drafted under the jurisdiction of an EU member state and EU consumer-protection law applies to all consumer contracts. Full legal-entity details are shared with clients on invoicing.
          </p>
        </div>
      </section>

      <section className="max-w-4xl mx-auto px-4 py-12">
        <h2 className="text-2xl font-extrabold text-zinc-950 mb-6">What we stand for</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <Value icon={Zap} title="Speed is a feature"
            body="Your idea either finds traction or it doesn't. We get you to evidence in weeks, not quarters." />
          <Value icon={ShieldCheck} title="Fixed price, fixed scope"
            body="We quote once. No hourly creep, no sneaky change orders — any additions are transparent and explicitly approved." />
          <Value icon={Users} title="You own everything"
            body="Source code, data, and brand all transfer to you on final payment. We keep nothing behind a paywall." />
          <Value icon={Globe} title="EU-grade privacy"
            body="GDPR, NDA, and EU data residency by default — compatible with healthcare, finance, and regulated sectors." />
        </div>
      </section>

      <section className="max-w-4xl mx-auto px-4 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <ReferFriend shareUrl={typeof window !== "undefined" ? window.location.origin : "https://fastprototype.ai"} />
          <SocialShare />
        </div>
      </section>

      <section className="max-w-4xl mx-auto px-4 py-12">
        <div className="bg-gradient-to-br from-zinc-950 to-zinc-800 rounded-3xl p-8 text-white text-center">
          <h2 className="text-2xl sm:text-3xl font-extrabold mb-2">Got an idea? Let's price it.</h2>
          <p className="text-sm text-zinc-300 mb-6">AI generates your scope and fixed quote in under a minute.</p>
          <Link to="/build" data-testid="about-cta-start"
            className="inline-flex items-center gap-2 px-6 py-3 bg-white text-zinc-950 font-bold text-sm rounded-full hover:bg-zinc-100 transition-colors">
            Start my project <ArrowRight size={14} />
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
